import { useCallback, useMemo, useState } from "react";
import { ActivityIndicator, ScrollView } from "react-native";
import { Stack, useRouter } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { View, Text, Banner } from "@src/components";
import { CameraThumbnailCard, cameraThumbnailGridStyles } from "@src/features/cameras/components/CameraThumbnailCard";
import { useSiteCameraKeys } from "@src/features/cameras/hooks/useSiteCameraKeys";
import { getCameraKeyString } from "@src/utils/key-parser";
import { FloatingSelectionBar } from "@src/features/settings/components/FloatingSelectionBar";
import { useAlarmsByCameras } from "@src/services/api/hooks";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { groupCamerasByRoutine, RoutineGroupHeader, useRoutineMap, type RoutineGroup } from "./routine-shared";

export default function ManageRoutines() {
  const { theme } = useUnistyles();
  const { bottom } = useSafeAreaInsets();
  const router = useRouter();
  const { siteCameras, cameraKeys, selectedSiteId } = useSiteCameraKeys();
  const effectiveSiteId = selectedSiteId ?? "";
  const { headerLeft } = useHeaderBackButton();

  const [selectedCameraKeys, setSelectedCameraKeys] = useState<Set<string>>(new Set());
  const alarmQueries = useAlarmsByCameras(effectiveSiteId, cameraKeys);

  const isLoadingAlarms = alarmQueries.some((q) => q.isLoading);

  const routineMap = useRoutineMap(siteCameras, alarmQueries);

  const routineGroups = useMemo(() => groupCamerasByRoutine(siteCameras, routineMap), [siteCameras, routineMap]);

  const toggleCamera = useCallback((cameraKey: string) => {
    setSelectedCameraKeys((prev) => {
      const next = new Set(prev);
      if (next.has(cameraKey)) {
        next.delete(cameraKey);
      } else {
        next.add(cameraKey);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback((group: RoutineGroup) => {
    const groupKeys = group.cameras.map((c) => getCameraKeyString(c));
    setSelectedCameraKeys((prev) => {
      const allSelected = groupKeys.every((key) => prev.has(key));
      const next = new Set(prev);
      if (allSelected) {
        groupKeys.forEach((key) => next.delete(key));
      } else {
        groupKeys.forEach((key) => next.add(key));
      }
      return next;
    });
  }, []);

  const handleChangeRoutine = useCallback(() => {
    if (selectedCameraKeys.size === 0) return;
    router.push({
      pathname: "/alarm-settings/change-routine",
      params: { cameraKeys: JSON.stringify(Array.from(selectedCameraKeys)) },
    });
  }, [selectedCameraKeys, router]);

  return (
    <>
      <Stack.Screen options={{ title: "Manage routines", headerLeft }} />
      <ScrollView
        style={{ flex: 1, backgroundColor: theme.colors.background }}
        contentContainerStyle={{
          paddingHorizontal: theme.spacing["3xl"],
          paddingTop: theme.spacing["4xl"],
          paddingBottom: bottom + theme.spacing["6xl"],
          flexGrow: 1,
        }}
        showsVerticalScrollIndicator={false}
      >
        <Banner title="Select from the cameras below to change their routines." variant="info" />

        {isLoadingAlarms && (
          <View mt="7xl" alignItems="center">
            <ActivityIndicator color={theme.colors.text} />
          </View>
        )}

        {!isLoadingAlarms &&
          routineGroups.map((group) => {
            const groupKeys = group.cameras.map((c) => getCameraKeyString(c));
            const allSelected = groupKeys.every((key) => selectedCameraKeys.has(key));

            return (
              <View
                key={group.key}
                mt="3xl"
                style={{
                  borderBottomWidth: 1,
                  borderColor: theme.colors.borderCard,
                  paddingBottom: theme.spacing["5xl"],
                }}
              >
                <RoutineGroupHeader
                  routine={group.routine}
                  allSelected={allSelected}
                  onSelectAll={() => toggleSelectAll(group)}
                />
                <View style={cameraThumbnailGridStyles.grid} mt="4xl">
                  {group.cameras.map((camera) => {
                    const cameraKey = getCameraKeyString(camera);
                    const isSelected = selectedCameraKeys.has(cameraKey);

                    return (
                      <View key={cameraKey} style={cameraThumbnailGridStyles.gridItem}>
                        <CameraThumbnailCard
                          camera={camera}
                          isSelected={isSelected}
                          onToggle={() => toggleCamera(cameraKey)}
                        />
                      </View>
                    );
                  })}
                </View>
              </View>
            );
          })}
      </ScrollView>
      <FloatingSelectionBar
        selectedCount={selectedCameraKeys.size}
        actionLabel="Change routine"
        onAction={handleChangeRoutine}
        bottomInset={bottom}
      />
    </>
  );
}
